/**
 * The app frame.
 *
 * One structure, two shapes: a labelled rail on a desk, a five-item tab bar on
 * a phone. The tab bar carries only what a tech actually opens one-handed on
 * site; everything else moves into "More". A rail crammed into a phone, or a
 * tab bar with nine items in it, serves neither.
 */
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { initials } from "../lib/format";
import { Mark } from "./Mark";
import { Icon, type IconName } from "./Icon";

interface Item {
  to: string;
  label: string;
  short: string;
  icon: IconName;
  /** Shown in the phone tab bar rather than behind "More". */
  primary?: boolean;
  group: "run" | "sell" | "know";
}

const ITEMS: Item[] = [
  { to: "/", label: "Dispatch", short: "Today", icon: "calendar", primary: true, group: "run" },
  { to: "/jobs", label: "Jobs", short: "Jobs", icon: "list", primary: true, group: "run" },
  { to: "/projects", label: "Projects", short: "Projects", icon: "list", group: "run" },
  { to: "/field", label: "Field app", short: "Field", icon: "phone", primary: true, group: "run" },
  { to: "/customers", label: "Customers", short: "Customers", icon: "people", group: "sell" },
  { to: "/quotes", label: "Quotes", short: "Quotes", icon: "doc", primary: true, group: "sell" },
  { to: "/invoices", label: "Invoices", short: "Bills", icon: "receipt", group: "sell" },
  { to: "/sites", label: "Sites & assets", short: "Sites", icon: "site", group: "sell" },
  { to: "/catalogue", label: "Catalogue", short: "Catalogue", icon: "box", group: "sell" },
  { to: "/stock", label: "Stock", short: "Stock", icon: "box", group: "sell" },
  { to: "/contracts", label: "Contracts", short: "Contracts", icon: "repeat", group: "sell" },
  { to: "/compliance", label: "Compliance", short: "Compliance", icon: "shield", group: "know" },
  { to: "/insight", label: "Insight", short: "Insight", icon: "chart", group: "know" },
  { to: "/settings", label: "Settings", short: "Settings", icon: "cog", group: "know" },
];

const GROUPS: { key: Item["group"]; label: string }[] = [
  { key: "run", label: "Run the day" },
  { key: "sell", label: "Win & bill" },
  { key: "know", label: "Know" },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { session, signOut } = useAuth();
  const location = useLocation();
  const org = useQuery({ queryKey: ["org"], queryFn: () => api.org.get() });
  const items = ITEMS.filter(i => (i.to !== "/projects" || (org.data?.projects_enabled && ["owner", "office", "supervisor"].includes(session?.role ?? ""))) && (i.to !== "/catalogue" || ["owner", "office"].includes(session?.role ?? "")));
  const [moreOpen, setMoreOpen] = useState(false);

  // A route change closes the sheet — otherwise it hangs over the new screen.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  const primary = items.filter((i) => i.primary);
  const secondary = items.filter((i) => !i.primary);

  return (
    <div className="shell">
      <nav className="rail" aria-label="Main">
        <div className="brand">
          <Mark size={30} />
          <span>
            <b>Keystone</b>
            <small>{org.data?.trading_name || org.data?.name || "Trade operations"}</small>
          </span>
        </div>

        {GROUPS.map((group) => (
          <div key={group.key} className="rail-group">
            <span className="rail-group-label">{group.label}</span>
            {items.filter((i) => i.group === group.key).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `rail-link${isActive ? " active" : ""}`}
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="rail-foot">
          <div className="who">
            <span className="avatar">{initials(session?.fullName || session?.email || "?")}</span>
            <span className="who-text">
              <b title={session?.email}>{session?.fullName || session?.email || "Not signed in"}</b>
              <small>{session?.role ?? ""}</small>
            </span>
          </div>
          <button className="link-button" type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </nav>

      <main className="main">
        <div className="main-scroll">{children}</div>
      </main>

      {/* Phone tab bar. Hidden above the breakpoint by CSS, not unmounted, so
          navigation state survives a rotation. */}
      <nav className="tabbar" aria-label="Main">
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) => `tab${isActive ? " active" : ""}`}
          >
            <Icon name={item.icon} />
            <span>{item.short}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`tab${moreOpen ? " active" : ""}`}
          onClick={() => setMoreOpen((v) => !v)}
          aria-expanded={moreOpen}
          aria-controls="more-sheet"
        >
          <Icon name="more" />
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <>
          <button
            type="button"
            className="sheet-scrim"
            aria-label="Close menu"
            onClick={() => setMoreOpen(false)}
          />
          <div className="sheet" id="more-sheet" role="dialog" aria-label="More">
            <div className="sheet-grip" aria-hidden="true" />
            <div className="sheet-links">
              {secondary.map((item) => (
                <NavLink key={item.to} to={item.to} className="sheet-link">
                  <Icon name={item.icon} />
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="sheet-foot">
              <span className="who-text">
                <b>{session?.fullName || session?.email}</b>
                <small>{session?.role}</small>
              </span>
              <button className="btn" type="button" onClick={signOut}>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
